import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {Redirect} from "react-router-dom";

import {View} from "@khanacademy/wonder-blocks-core";
import {HeadingLarge} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import Log, {Errors} from "@khan/logging";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import Error from "./error.tsx";
import {profileLessFiles, type Profile} from "./profile-util.ts";

type OwnProps = {
    kaid: string;
    profilePromise: Promise<Profile>;
    isCoachListReadable: boolean;
    onSufficientlyUsable: () => void;
    onClassChange?: (districtClassCode?: string | null | undefined) => void;
};

type Props = OwnProps &
    WithUserProfileProps &
    WithMarkConversionProps &
    WithApolloProps &
    WithLocationProps;

type State = {
    loading: boolean;
    showError: boolean;
};

class Teachers extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;

    state: State = {
        loading: true,
        showError: false,
    };

    componentDidMount() {
        if (!this.props.profileLoading) {
            this.updateLegacyCoaches();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const profileLoadingChanged =
            prevProps.profileLoading && !this.props.profileLoading;
        const kaidChanged = prevProps.kaid !== this.props.kaid;

        if (profileLoadingChanged || kaidChanged) {
            this.updateLegacyCoaches();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    updateLegacyCoaches() {
        const {
            profilePromise,
            markConversion,
            onSufficientlyUsable,
            profile: userProfile,
        } = this.props;

        const promises = [
            profilePromise,
            import("../../profile-package/coaches.tsx"),
            Promise.all(profileLessFiles()),
        ];
        markConversion({id: "logged_in_homepage_visit"});

        Promise.all(promises)
            // @ts-expect-error - TS2345 - Argument of type '([profile, { default: LegacyCoaches }]: [any, any]) => void' is not assignable to parameter of type '(value: (unknown[] | Profile | typeof import("/Users/kevinbarabash/khan/webapp/apps/khanacademy/src/profile-package/coaches"))[]) => void | PromiseLike<void>'.
            .then(([profile, {default: LegacyCoaches}]: [any, any]) => {
                if (!this._isMounted) {
                    return;
                }

                this.setState({loading: false});

                LegacyCoaches.initialize(
                    this.props.gqlClient,
                    profile.viewPermissions.isCoachListWritable,
                    "lg",
                    (districtClassCode?: string | null) =>
                        this.handleClassChange(districtClassCode),
                    userProfile, // profile of user who is viewing this page
                );

                onSufficientlyUsable();
            })
            .catch((error) => {
                if (this._isMounted) {
                    Log.error(
                        "Error initializing Coaches profile:",
                        Errors.Internal,
                        {cause: error},
                    );
                    this.setState({showError: true});
                }
            });

        this._isMounted = true;
    }

    handleClassChange(districtClassCode?: string | null) {
        this.props.onClassChange && this.props.onClassChange(districtClassCode);
    }

    maybeRenderRedirect() {
        const {location} = this.props;

        const {joinedClassCode, confirmJoinClassCode} = location.queryParams;

        if (confirmJoinClassCode) {
            return <Redirect to={`/join/${confirmJoinClassCode}`} />;
        }

        if (joinedClassCode) {
            return <Redirect to={`/profile/me/class/${joinedClassCode}`} />;
        }
    }

    render(): React.ReactNode {
        const {loading, showError} = this.state;

        if (showError) {
            return <Error />;
        }

        return (
            <View style={styles.container} testId="my-teachers">
                {this.maybeRenderRedirect()}
                <HeadingLarge>{t`My teachers and tutors`}</HeadingLarge>
                {loading && <RouteLoadSpinner />}
                <div id="tab-content-coaches" className="lihp-coaches" />
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 32,
    },
});

type ExportProps = WithoutLocation<
    WithoutApollo<
        WithoutMarkConversion<
            WithoutUserProfile<
                JSX.LibraryManagedAttributes<
                    typeof Teachers,
                    React.ComponentProps<typeof Teachers>
                >
            >
        >
    >
>;
export default compose(
    withUserProfile,
    withMarkConversion,
    withApollo,
    withLocation,
)(Teachers) as React.ComponentType<ExportProps>;
